import React, { useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Warning,
  Info as InfoIcon,
  Speed,
  ShowChart,
  AccountBalance,
} from '@mui/icons-material';
import { WidgetProps } from '../types/index.js';

interface StockData {
  symbol: string;
  price: number;
  volume: number;
  change?: number;
  changePercent?: number;
}

interface PortfolioData {
  stocks: StockData[];
  portfolioScore?: {
    diversification: number;
    sentiment: number;
    overall: number;
  };
}

export const RiskMetricsWidget: React.FC<WidgetProps> = ({
  widget,
  onUpdateOutputs,
}) => {
  // Get connected inputs
  const portfolioData = widget.inputs.find(input => input.id === 'portfolioData')?.value as PortfolioData | undefined;
  const marketSentiment = widget.inputs.find(input => input.id === 'marketSentiment')?.value as string | undefined;

  // Calculate risk metrics
  const riskMetrics = useMemo(() => {
    if (!portfolioData?.stocks || portfolioData.stocks.length === 0) {
      return null;
    }

    const stocks = portfolioData.stocks;
    const prices = stocks.map(s => s.price);
    const volumes = stocks.map(s => s.volume);

    // Portfolio statistics
    const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
    const totalVolume = volumes.reduce((a, b) => a + b, 0);
    
    // Volatility (simplified standard deviation)
    const variance = prices.reduce((sum, price) => sum + Math.pow(price - avgPrice, 2), 0) / prices.length;
    const volatility = Math.sqrt(variance) / avgPrice * 100;

    // Beta calculation (simplified - comparing to market average)
    const beta = prices.map(p => (p - avgPrice) / avgPrice).reduce((a, b) => a + b, 0) / prices.length * 1.2;

    // Sharpe Ratio (simplified)
    const riskFreeRate = 2; // 2% risk-free rate
    const portfolioReturn = ((avgPrice - 100) / 100) * 100; // Assume 100 as baseline
    const sharpeRatio = (portfolioReturn - riskFreeRate) / volatility;

    // Value at Risk (VaR) - 95% confidence
    const var95 = avgPrice * 0.05 * volatility / 100;

    // Concentration risk
    const maxPrice = Math.max(...prices);
    const minPrice = Math.min(...prices);
    const priceSpread = ((maxPrice - minPrice) / avgPrice) * 100;

    return {
      volatility,
      beta: Math.abs(beta),
      sharpeRatio,
      var95,
      priceSpread,
      avgPrice,
      totalVolume,
      stockCount: stocks.length,
      concentrationRisk: priceSpread > 50 ? 'high' : priceSpread > 25 ? 'medium' : 'low',
      overallRisk: volatility > 30 ? 'high' : volatility > 15 ? 'medium' : 'low',
    };
  }, [portfolioData]);

  // Risk score calculation
  const riskScore = useMemo(() => {
    if (!riskMetrics) return null;

    // Weight different factors
    const volatilityScore = Math.max(0, 100 - riskMetrics.volatility * 2);
    const betaScore = Math.max(0, 100 - Math.abs(1 - riskMetrics.beta) * 50);
    const sharpeScore = Math.min(100, Math.max(0, riskMetrics.sharpeRatio * 25 + 50));
    const concentrationScore = riskMetrics.concentrationRisk === 'low' ? 80 : 
                              riskMetrics.concentrationRisk === 'medium' ? 50 : 20;

    const overall = (volatilityScore + betaScore + sharpeScore + concentrationScore) / 4;

    return {
      volatility: volatilityScore,
      beta: betaScore,
      sharpe: sharpeScore,
      concentration: concentrationScore,
      overall,
    };
  }, [riskMetrics]);

  // Use ref to track if we've already sent outputs for current data
  const lastOutputRef = useRef<string>('');

  // Update outputs
  useEffect(() => {
    if (!riskMetrics || !riskScore) return;

    // Create a stable key from the metrics
    const outputKey = JSON.stringify({
      vol: riskMetrics.volatility.toFixed(2),
      beta: riskMetrics.beta.toFixed(2),
      sharpe: riskMetrics.sharpeRatio.toFixed(2),
      risk: riskMetrics.overallRisk,
      score: riskScore.overall.toFixed(0),
    });

    // Only update if the key changed
    if (outputKey !== lastOutputRef.current) {
      lastOutputRef.current = outputKey;
      onUpdateOutputs({
        riskMetrics,
        riskScore,
        recommendation: riskScore.overall > 70 ? 'low-risk' : riskScore.overall > 40 ? 'moderate-risk' : 'high-risk',
      });
    }
  }, [riskMetrics, riskScore, onUpdateOutputs]);

  const getRiskColor = (risk: string): 'error' | 'warning' | 'success' => {
    switch (risk) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'warning';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'success';
    if (score >= 40) return 'warning';
    return 'error';
  };

  if (!portfolioData || !riskMetrics) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Alert severity="info">
          Connect portfolio data to calculate risk metrics
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Risk Analysis</Typography>
          <Chip 
            label={`${portfolioData.stocks.length} Stocks`}
            size="small"
            icon={<AccountBalance />}
          />
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
        <Grid container spacing={2}>
          {/* Key Risk Indicators */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Key Risk Indicators
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Speed color={getRiskColor(riskMetrics.overallRisk)} sx={{ fontSize: 40 }} />
                    <Typography variant="h6">{riskMetrics.volatility.toFixed(1)}%</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Volatility
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <ShowChart color="primary" sx={{ fontSize: 40 }} />
                    <Typography variant="h6">{riskMetrics.beta.toFixed(2)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Beta
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <TrendingUp color={riskMetrics.sharpeRatio > 1 ? 'success' : 'warning'} sx={{ fontSize: 40 }} />
                    <Typography variant="h6">{riskMetrics.sharpeRatio.toFixed(2)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Sharpe Ratio
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Warning color={getRiskColor(riskMetrics.concentrationRisk)} sx={{ fontSize: 40 }} />
                    <Typography variant="h6">${riskMetrics.var95.toFixed(2)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      VaR (95%)
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Risk Scores */}
          {riskScore && (
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Risk Scores
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Volatility Risk" />
                    <Box sx={{ width: 100, mr: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={riskScore.volatility}
                        color={getScoreColor(riskScore.volatility)}
                      />
                    </Box>
                    <Typography variant="body2">{riskScore.volatility.toFixed(0)}%</Typography>
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Market Risk (Beta)" />
                    <Box sx={{ width: 100, mr: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={riskScore.beta}
                        color={getScoreColor(riskScore.beta)}
                      />
                    </Box>
                    <Typography variant="body2">{riskScore.beta.toFixed(0)}%</Typography>
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Risk-Adjusted Return" />
                    <Box sx={{ width: 100, mr: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={riskScore.sharpe}
                        color={getScoreColor(riskScore.sharpe)}
                      />
                    </Box>
                    <Typography variant="body2">{riskScore.sharpe.toFixed(0)}%</Typography>
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Concentration Risk" />
                    <Box sx={{ width: 100, mr: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={riskScore.concentration}
                        color={getScoreColor(riskScore.concentration)}
                      />
                    </Box>
                    <Typography variant="body2">{riskScore.concentration.toFixed(0)}%</Typography>
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          )}

          {/* Overall Assessment */}
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Portfolio Assessment
              </Typography>
              
              <Box sx={{ textAlign: 'center', my: 2 }}>
                <Typography variant="h3" color={getScoreColor(riskScore?.overall || 0)}>
                  {riskScore?.overall.toFixed(0)}%
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Overall Risk Score
                </Typography>
                <Chip 
                  label={riskMetrics.overallRisk.toUpperCase() + ' RISK'}
                  color={getRiskColor(riskMetrics.overallRisk)}
                  sx={{ mt: 1 }}
                />
              </Box>

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Concentration"
                    secondary={`${riskMetrics.priceSpread.toFixed(1)}% price spread across ${riskMetrics.stockCount} stocks`}
                  />
                </ListItem>
                {marketSentiment && (
                  <ListItem>
                    <ListItemIcon>
                      {marketSentiment === 'bullish' ? <TrendingUp color="success" /> : 
                       marketSentiment === 'bearish' ? <TrendingDown color="error" /> :
                       <Warning color="warning" />}
                    </ListItemIcon>
                    <ListItemText 
                      primary="Market Sentiment"
                      secondary={marketSentiment.charAt(0).toUpperCase() + marketSentiment.slice(1)}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>

          {/* Recommendations */}
          <Grid item xs={12}>
            <Alert 
              severity={riskScore && riskScore.overall > 70 ? 'success' : 
                       riskScore && riskScore.overall > 40 ? 'warning' : 'error'}
              icon={<InfoIcon />}
            >
              <Typography variant="subtitle2" gutterBottom>
                Risk Management Recommendation
              </Typography>
              <Typography variant="body2">
                {riskScore && riskScore.overall > 70 ? 
                  'Portfolio shows balanced risk profile. Consider maintaining current allocation.' :
                 riskScore && riskScore.overall > 40 ?
                  'Moderate risk detected. Review high-volatility positions and consider diversification.' :
                  'High risk profile. Strong recommendation to rebalance portfolio and reduce concentration.'}
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};